import { useRef, useState } from 'react';
import { reportsApi } from '../../../services/api/reports.api';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { ReportFieldValue, ReportFormValues, ReportGeneration, ReportSchema, ReportTypeSummary } from '../types';
import {
  buildInitialValues,
  buildReportGeneration,
  buildReportSummary,
  validateFields,
} from '../components/reportForm/reportFormUtils';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

// Backs the "+ → Reports" menu and the report form popup. Nothing in here
// branches on report_key or field_key. `sendReport` sends the form as a chat
// message: `query` is the readable summary, `reportGeneration` the answers.
export const useReportForm = (sendReport: (query: string, reportGeneration: ReportGeneration) => void) => {
  const [reportTypes, setReportTypes] = useState<ReportTypeSummary[]>([]);
  const [reportTypesStatus, setReportTypesStatus] = useState<LoadStatus>('idle');
  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);
  const reportMenuRef = useRef<HTMLDivElement>(null);
  // The "Reports" row that toggles the flyout; counts as inside so it can close it.
  const reportMenuTriggerRef = useRef<HTMLButtonElement>(null);

  // The menu item picked: its id fetches the form, its name titles it.
  const [activeReport, setActiveReport] = useState<ReportTypeSummary | null>(null);
  const [reportSchema, setReportSchema] = useState<ReportSchema | null>(null);
  const [schemaStatus, setSchemaStatus] = useState<LoadStatus>('idle');
  const [formValues, setFormValues] = useState<ReportFormValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useClickOutside([reportMenuRef, reportMenuTriggerRef], () => setIsReportMenuOpen(false), isReportMenuOpen);

  // The list loads the first time the flyout opens; after a failure,
  // reopening it tries again.
  const toggleReportMenu = async () => {
    const opening = !isReportMenuOpen;
    setIsReportMenuOpen(opening);
    if (!opening || reportTypesStatus === 'ready' || reportTypesStatus === 'loading') return;
    setReportTypesStatus('loading');
    try {
      setReportTypes(await reportsApi.getReportTypes());
      setReportTypesStatus('ready');
    } catch (e) {
      console.error('[report-form] getReportTypes error:', e);
      setReportTypesStatus('error');
    }
  };

  // Fetches the form fresh every time, so config changes show without a reload.
  const selectReportType = async (report: ReportTypeSummary) => {
    setIsReportMenuOpen(false);
    setActiveReport(report);
    setReportSchema(null);
    setFieldErrors({});
    setSchemaStatus('loading');
    try {
      const schema = await reportsApi.getReportSchema(report.report_id);
      setReportSchema(schema);
      setFormValues(buildInitialValues(schema.fields));
      setSchemaStatus('ready');
    } catch (e) {
      console.error('[report-form] getReportSchema error:', e);
      setSchemaStatus('error');
    }
  };

  const closeReportModal = () => {
    setActiveReport(null);
    setReportSchema(null);
    setSchemaStatus('idle');
    setFormValues({});
    setFieldErrors({});
  };

  const setFieldValue = (fieldKey: string, value: ReportFieldValue) => {
    setFormValues((prev) => ({ ...prev, [fieldKey]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const submitReport = () => {
    if (!reportSchema || !activeReport) return;
    const errors = validateFields(reportSchema.fields, formValues);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    sendReport(buildReportSummary(activeReport.report_name, reportSchema, formValues), buildReportGeneration(reportSchema, formValues));
    closeReportModal();
  };

  return {
    reportTypes,
    reportTypesStatus,
    isReportMenuOpen,
    reportMenuRef,
    reportMenuTriggerRef,
    toggleReportMenu,
    selectReportType,
    isReportOpen: schemaStatus !== 'idle',
    activeReport,
    reportSchema,
    schemaStatus,
    formValues,
    fieldErrors,
    closeReportModal,
    setFieldValue,
    submitReport,
  };
};
